import { useCallback, useEffect, useMemo, useState } from 'react'
import { CloudSyncOutlined, DeleteOutlined, HistoryOutlined, UploadOutlined } from '@ant-design/icons'
import { Button, Card, Form, Input, Modal, Popconfirm, Space, Switch, Table, Tag, Typography, message } from 'antd'
import { adminApi, type PageResult } from '../../api'
import { PageHeader, formatDateOrDash, tablePagination, useServerPagination } from '../../components/ui'
import { orgSyncSourceLabel, orgSyncTriggerLabel } from '../../lib/orgSyncLabels'
import './OrgSync.css'

type SyncLog = {
  id: number
  source: string
  source_label?: string
  trigger: string
  trigger_label?: string
  status: string
  stats_json: Record<string, number>
  error_message: string
  started_at: string | null
}

type SyncConfig = {
  enabled: boolean
  source: string
  pull_url: string
  auth_header_name: string
  auth_header_value: string
  auth_header_value_set?: boolean
}

const SAMPLE_JSON = `{
  "source": "external",
  "units": [
    { "external_id": "root", "parent_external_id": null, "name": "总公司", "code": "HQ", "unit_type": "branch", "sort_order": 0 },
    { "external_id": "rd", "parent_external_id": "root", "name": "研发部", "code": "RD", "unit_type": "department", "sort_order": 1 }
  ]
}`

export default function OrgSync() {
  const [configForm] = Form.useForm()
  const [pushForm] = Form.useForm()
  const [config, setConfig] = useState<SyncConfig | null>(null)
  const [logs, setLogs] = useState<SyncLog[]>([])
  const [logTotal, setLogTotal] = useState(0)
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [deletingLogId, setDeletingLogId] = useState<number | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [pushOpen, setPushOpen] = useState(false)
  const [logsOpen, setLogsOpen] = useState(false)
  const { page: logPage, pageSize: logPageSize, handlePaginationChange: onLogPageChange, paginationOptions: logPaginationOptions } =
    useServerPagination()

  const loadLogs = useCallback((p: number, ps: number) => {
    setLoadingLogs(true)
    return adminApi
      .get<PageResult<SyncLog>>('/admin/org-units/sync/logs', { params: { page: p, page_size: ps } })
      .then((r) => {
        setLogs(r.data.items)
        setLogTotal(r.data.total)
        return r.data
      })
      .catch((e) => {
        message.error(e.message)
        return null
      })
      .finally(() => setLoadingLogs(false))
  }, [])

  const loadConfig = useCallback(() => {
    adminApi
      .get<SyncConfig>('/admin/org-units/sync/config')
      .then((r) => {
        setConfig(r.data)
        configForm.setFieldsValue({
          enabled: r.data.enabled,
          source: r.data.source || 'external',
          pull_url: r.data.pull_url || '',
          auth_header_name: r.data.auth_header_name || '',
          auth_header_value: '',
        })
      })
      .catch((e) => message.error(e.message))
  }, [configForm])

  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  useEffect(() => {
    if (!logsOpen) return
    void loadLogs(logPage, logPageSize)
  }, [logPage, logPageSize, loadLogs, logsOpen])

  const openLogs = () => {
    setLogsOpen(true)
  }

  const removeLog = useCallback(
    (row: SyncLog) => {
      setDeletingLogId(row.id)
      adminApi
        .delete(`/admin/org-units/sync/logs/${row.id}`)
        .then(async () => {
          message.success('已删除')
          const data = await loadLogs(logPage, logPageSize)
          if (data && data.items.length === 0 && logPage > 1) {
            onLogPageChange(logPage - 1, logPageSize)
          }
        })
        .catch((e) => message.error(e.message))
        .finally(() => setDeletingLogId(null))
    },
    [loadLogs, logPage, logPageSize, onLogPageChange],
  )

  const logColumns = useMemo(
    () => [
      {
        title: '来源',
        dataIndex: 'source',
        width: 120,
        render: (v: string, row: SyncLog) => row.source_label || orgSyncSourceLabel(v, row.trigger),
      },
      {
        title: '方式',
        dataIndex: 'trigger',
        width: 100,
        render: (v: string, row: SyncLog) => row.trigger_label || orgSyncTriggerLabel(v),
      },
      {
        title: '状态',
        dataIndex: 'status',
        width: 80,
        render: (v: string) =>
          v === 'success' ? <Tag color="green">成功</Tag> : <Tag color="red">失败</Tag>,
      },
      {
        title: '数量',
        width: 72,
        render: (_: unknown, r: SyncLog) => r.stats_json?.total ?? '—',
      },
      {
        title: '时间',
        dataIndex: 'started_at',
        width: 168,
        render: (v: string | null) => formatDateOrDash(v),
      },
      { title: '错误', dataIndex: 'error_message', ellipsis: true },
      {
        title: '操作',
        width: 72,
        fixed: 'right' as const,
        render: (_: unknown, row: SyncLog) => (
          <Popconfirm title="确定删除该同步记录？" okText="删除" cancelText="取消" onConfirm={() => removeLog(row)}>
            <Button type="link" danger size="small" icon={<DeleteOutlined />} loading={deletingLogId === row.id}>
              删除
            </Button>
          </Popconfirm>
        ),
      },
    ],
    [deletingLogId, removeLog],
  )

  const saveConfig = () => {
    void configForm.validateFields().then((v) => {
      adminApi
        .put('/admin/org-units/sync/config', v)
        .then((r) => {
          message.success('配置已保存')
          setConfig(r.data)
        })
        .catch((e) => message.error(e.message))
    })
  }

  const runPull = () => {
    setSyncing(true)
    adminApi
      .post('/admin/org-units/sync/pull')
      .then((r) => {
        message.success(`拉取完成：${r.data.stats?.total ?? 0} 条`)
        if (logsOpen) void loadLogs(logPage, logPageSize)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setSyncing(false))
  }

  const openPush = () => {
    pushForm.resetFields()
    pushForm.setFieldsValue({ source: 'external', deactivate_missing: true, payload_json: SAMPLE_JSON })
    setPushOpen(true)
  }

  const submitPush = () => {
    void pushForm.validateFields().then((v) => {
      let payload: { source?: string; units: unknown[]; deactivate_missing?: boolean }
      try {
        payload = JSON.parse(v.payload_json)
      } catch {
        message.error('JSON 格式不正确')
        return
      }
      if (!Array.isArray(payload.units)) {
        message.error('JSON 须包含 units 数组')
        return
      }
      setSyncing(true)
      adminApi
        .post('/admin/org-units/sync', {
          source: payload.source || v.source || 'external',
          units: payload.units,
          deactivate_missing: v.deactivate_missing !== false,
        })
        .then((r) => {
          message.success(`推送完成：${r.data.stats?.total ?? 0} 条`)
          setPushOpen(false)
          if (logsOpen) void loadLogs(logPage, logPageSize)
        })
        .catch((e) => message.error(e.message))
        .finally(() => setSyncing(false))
    })
  }

  return (
    <div className="org-sync-page">
      <div className="org-sync-head">
        <PageHeader
          title="组织同步"
          description="配置外部系统拉取或手动推送 JSON。Excel 导入请在「组织架构」页操作；同步历史可在右上角查看。"
        />
        <Button icon={<HistoryOutlined />} onClick={openLogs}>
          同步记录
        </Button>
      </div>

      <Card title="拉取配置" size="small" className="org-sync-card">
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          启用后可通过 HTTP 接口定时或手动拉取组织数据，写入组织树（与 Excel 导入相互独立）。
        </Typography.Paragraph>
        <Form form={configForm} layout="vertical">
          <Form.Item name="enabled" label="启用拉取" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="source" label="来源标识" rules={[{ required: true }]}>
            <Input placeholder="如 ecology、ldap" />
          </Form.Item>
          <Form.Item name="pull_url" label="拉取 URL">
            <Input placeholder="https://oa.example.com/api/org-units" />
          </Form.Item>
          <Form.Item name="auth_header_name" label="鉴权 Header">
            <Input placeholder="Authorization" />
          </Form.Item>
          <Form.Item
            name="auth_header_value"
            label={config?.auth_header_value_set ? '鉴权值（已设置，留空不改）' : '鉴权值'}
          >
            <Input.Password placeholder="Bearer xxx" />
          </Form.Item>
          <Space wrap className="org-sync-actions">
            <Button type="primary" onClick={saveConfig}>
              保存配置
            </Button>
            <Button icon={<CloudSyncOutlined />} loading={syncing} onClick={runPull}>
              立即拉取
            </Button>
            <Button icon={<UploadOutlined />} onClick={openPush}>
              推送 JSON
            </Button>
          </Space>
        </Form>
      </Card>

      <Modal
        title="同步记录"
        open={logsOpen}
        onCancel={() => setLogsOpen(false)}
        footer={null}
        width={920}
        destroyOnClose
      >
        <Table<SyncLog>
          rowKey="id"
          size="small"
          loading={loadingLogs}
          dataSource={logs}
          scroll={{ x: 860 }}
          pagination={tablePagination(logPage, logTotal, onLogPageChange, logPaginationOptions)}
          columns={logColumns}
        />
      </Modal>

      <Modal
        title="推送组织数据（JSON）"
        open={pushOpen}
        onOk={submitPush}
        onCancel={() => setPushOpen(false)}
        width={720}
        confirmLoading={syncing}
      >
        <Form form={pushForm} layout="vertical">
          <Form.Item name="source" label="来源标识">
            <Input placeholder="external" />
          </Form.Item>
          <Form.Item name="deactivate_missing" label="停用未包含的节点" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="payload_json" label="JSON 数据" rules={[{ required: true }]}>
            <Input.TextArea rows={14} style={{ fontFamily: 'monospace' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
